import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from "react";
import { getAppDef } from "../lib/appRegistry";

export type SnapZone =
  | "top"       // maximize
  | "left"      // left 50%
  | "right"     // right 50%
  | "top-left"  // top-left 25%
  | "top-right" // top-right 25%
  | "bottom-left"  // bottom-left 25%
  | "bottom-right" // bottom-right 25%
  | null;

export interface WindowState {
  id: string;
  title: string;
  icon: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  zIndex: number;
  status: "open" | "minimized" | "maximized";
  /** The zone this window is snapped to, if any */
  snappedZone?: SnapZone;
  /** Geometry before snapping, used to restore on unsnap */
  preSnapGeometry?: { position: { x: number; y: number }; size: { width: number; height: number } };
}

export interface LayoutPreset {
  id: string;
  name: string;
  windows: Array<{
    id: string;
    position: { x: number; y: number };
    size: { width: number; height: number };
    status: "open" | "minimized" | "maximized";
  }>;
}

interface WindowManagerContextType {
  windows: WindowState[];
  activeId: string | null;
  initialized: boolean;
  openWindow: (id: string) => void;
  closeWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  maximizeWindow: (id: string) => void;
  restoreWindow: (id: string) => void;
  focusWindow: (id: string) => void;
  moveWindow: (id: string, pos: { x: number; y: number }) => void;
  resizeWindow: (id: string, size: { width: number; height: number }) => void;
  snapWindow: (id: string, zone: SnapZone) => void;
  unsnapWindow: (id: string) => void;
  minimizeAll: () => void;
  restoreAll: () => void;
  closeAllWindows: () => void;
  cascadeWindows: () => void;
  tileHorizontally: () => void;
  tileVertically: () => void;
  setAlwaysOnTop: (id: string, on: boolean) => void;
  cycleWindow: (direction?: "next" | "prev") => string | null;
  applyPreset: (presetId: string) => void;
  saveCurrentLayout: (name: string) => void;
  loadSavedLayouts: () => LayoutPreset[];
  deleteSavedLayout: (id: string) => void;
  getBuiltInPresets: () => LayoutPreset[];
}

const WindowManagerContext = createContext<WindowManagerContextType | null>(null);

const DEFAULT_SIZE = { width: 900, height: 620 };
const CASCADE_ORIGIN = { x: 80, y: 60 };
const CASCADE_STEP = { x: 28, y: 28 };
const BASE_Z = 10;

const STORAGE_KEY_LAYOUTS = "opentool-saved-layouts";
const STORAGE_KEY_LAST_STATE = "opentool-last-window-state";

function cascadePosition(index: number): { x: number; y: number } {
  const maxX = typeof window !== "undefined" ? window.innerWidth - 400 : 1000;
  const maxY = typeof window !== "undefined" ? window.innerHeight - 300 : 600;
  const x = CASCADE_ORIGIN.x + (index * CASCADE_STEP.x) % maxX;
  const y = CASCADE_ORIGIN.y + (index * CASCADE_STEP.y) % maxY;
  return { x, y };
}

function getBuiltInPresets(): LayoutPreset[] {
  const w = typeof window !== "undefined" ? window.innerWidth : 1920;
  const h = typeof window !== "undefined" ? window.innerHeight - 48 : 1032; // minus taskbar

  return [
    {
      id: "focus",
      name: "Focus",
      windows: [
        { id: "chat", position: { x: 0, y: 0 }, size: { width: w, height: h }, status: "maximized" },
      ],
    },
    {
      id: "dashboard",
      name: "Dashboard",
      windows: [
        { id: "home", position: { x: 0, y: 0 }, size: { width: Math.floor(w * 0.6), height: h }, status: "open" },
        { id: "chat", position: { x: Math.floor(w * 0.6), y: 0 }, size: { width: Math.floor(w * 0.4), height: h }, status: "open" },
      ],
    },
    {
      id: "command-center",
      name: "Command Center",
      windows: [
        { id: "home", position: { x: 0, y: 0 }, size: { width: Math.floor(w * 0.5), height: Math.floor(h * 0.5) }, status: "open" },
        { id: "chat", position: { x: 0, y: Math.floor(h * 0.5) }, size: { width: Math.floor(w * 0.5), height: Math.floor(h * 0.5) }, status: "open" },
        { id: "files", position: { x: Math.floor(w * 0.5), y: 0 }, size: { width: Math.floor(w * 0.5), height: h }, status: "open" },
      ],
    },
    {
      id: "monitor",
      name: "Monitor",
      windows: [
        { id: "home", position: { x: 0, y: 0 }, size: { width: w, height: h }, status: "maximized" },
      ],
    },
    {
      id: "development",
      name: "Development",
      windows: [
        { id: "files", position: { x: 0, y: 0 }, size: { width: Math.floor(w * 0.6), height: h }, status: "open" },
        { id: "chat", position: { x: Math.floor(w * 0.6), y: 0 }, size: { width: Math.floor(w * 0.4), height: Math.floor(h * 0.5) }, status: "open" },
        { id: "browser", position: { x: Math.floor(w * 0.6), y: Math.floor(h * 0.5) }, size: { width: Math.floor(w * 0.4), height: Math.floor(h * 0.5) }, status: "open" },
      ],
    },
    {
      id: "writer",
      name: "Writer",
      windows: [
        { id: "notebook", position: { x: 0, y: 0 }, size: { width: Math.floor(w * 0.55), height: h }, status: "open" },
        { id: "chat", position: { x: Math.floor(w * 0.55), y: 0 }, size: { width: Math.floor(w * 0.45), height: h }, status: "open" },
      ],
    },
  ];
}

function loadLastState(): WindowState[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_LAST_STATE);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    // Reject stale state where every window is minimized (appears as empty desktop)
    const hasVisible = parsed.some((w: WindowState) => w.status !== "minimized");
    if (!hasVisible) return null;
    return parsed;
  } catch { /* ignore */ }
  return null;
}

function saveLastState(windows: WindowState[]) {
  try {
    localStorage.setItem(STORAGE_KEY_LAST_STATE, JSON.stringify(windows));
  } catch { /* ignore */ }
}

export function WindowManagerProvider({ children }: { children: ReactNode }) {
  const [windows, setWindows] = useState<WindowState[]>([]);
  const [nextZ, setNextZ] = useState(BASE_Z);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [windowCount, setWindowCount] = useState(0);
  const [initialized, setInitialized] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Persist window state on changes (debounced)
  useEffect(() => {
    if (!initialized) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      saveLastState(windows);
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [windows, initialized]);

  // Restore last layout on mount (only if no hash)
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!hash) {
      const lastState = loadLastState();
      if (lastState && lastState.length > 0) {
        setWindows(lastState);
        setNextZ(BASE_Z + lastState.length);
        setWindowCount(lastState.length);
        const lastActive = lastState.find(w => w.status !== "minimized");
        if (lastActive) setActiveId(lastActive.id);
        setInitialized(true);
        return;
      }
    }
    setInitialized(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openWindow = useCallback((id: string) => {
    setWindows((prev) => {
      const existing = prev.find((w) => w.id === id);
      if (existing) {
        if (existing.status === "minimized") {
          return prev.map((w) =>
            w.id === id ? { ...w, status: "open" as const, zIndex: nextZ + 1 } : w,
          );
        }
        return prev.map((w) =>
          w.id === id ? { ...w, zIndex: nextZ + 1 } : w,
        );
      }

      const app = getAppDef(id);
      const size = app?.defaultSize ?? DEFAULT_SIZE;
      const pos = cascadePosition(windowCount);

      const newWindow: WindowState = {
        id,
        title: app?.label ?? id,
        icon: app?.icon ?? "",
        position: pos,
        size,
        zIndex: nextZ + 1,
        status: "open",
      };

      return [...prev, newWindow];
    });

    setNextZ((z) => z + 1);
    setActiveId(id);
    setWindowCount((c) => c + 1);
  }, [nextZ, windowCount]);

  const closeWindow = useCallback((id: string) => {
    setWindows((prev) => prev.filter((w) => w.id !== id));
    setActiveId((prev) => (prev === id ? null : prev));
  }, []);

  const minimizeWindow = useCallback((id: string) => {
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, status: "minimized" as const } : w)),
    );
    setActiveId((prev) => (prev === id ? null : prev));
  }, []);

  const maximizeWindow = useCallback((id: string) => {
    setWindows((prev) =>
      prev.map((w) =>
        w.id === id
          ? { ...w, status: "maximized" as const, zIndex: nextZ + 1 }
          : w,
      ),
    );
    setNextZ((z) => z + 1);
    setActiveId(id);
  }, [nextZ]);

  const restoreWindow = useCallback((id: string) => {
    setWindows((prev) =>
      prev.map((w) =>
        w.id === id
          ? { ...w, status: "open" as const, zIndex: nextZ + 1 }
          : w,
      ),
    );
    setNextZ((z) => z + 1);
    setActiveId(id);
  }, [nextZ]);

  const focusWindow = useCallback((id: string) => {
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, zIndex: nextZ + 1 } : w)),
    );
    setNextZ((z) => z + 1);
    setActiveId(id);
  }, [nextZ]);

  const moveWindow = useCallback((id: string, pos: { x: number; y: number }) => {
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, position: pos } : w)),
    );
  }, []);

  const resizeWindow = useCallback(
    (id: string, size: { width: number; height: number }) => {
      setWindows((prev) =>
        prev.map((w) => (w.id === id ? { ...w, size } : w)),
      );
    },
    [],
  );

  // Track pre-minimize states for restoreAll
  const minimizedSnapshotRef = useRef<WindowState[] | null>(null);

  const snapWindow = useCallback((id: string, zone: SnapZone) => {
    if (!zone) return;
    const w = typeof window !== "undefined" ? window.innerWidth : 1920;
    const h = typeof window !== "undefined" ? window.innerHeight - 48 : 1032;
    const halfW = Math.floor(w / 2);
    const halfH = Math.floor(h / 2);

    let pos: { x: number; y: number };
    let size: { width: number; height: number };

    switch (zone) {
      case "top":
        pos = { x: 0, y: 0 };
        size = { width: w, height: h };
        break;
      case "left":
        pos = { x: 0, y: 0 };
        size = { width: halfW, height: h };
        break;
      case "right":
        pos = { x: halfW, y: 0 };
        size = { width: w - halfW, height: h };
        break;
      case "top-left":
        pos = { x: 0, y: 0 };
        size = { width: halfW, height: halfH };
        break;
      case "top-right":
        pos = { x: halfW, y: 0 };
        size = { width: w - halfW, height: halfH };
        break;
      case "bottom-left":
        pos = { x: 0, y: halfH };
        size = { width: halfW, height: h - halfH };
        break;
      case "bottom-right":
        pos = { x: halfW, y: halfH };
        size = { width: w - halfW, height: h - halfH };
        break;
      default:
        return;
    }

    setWindows((prev) =>
      prev.map((win) => {
        if (win.id !== id) return win;
        return {
          ...win,
          status: "open" as const,
          position: pos,
          size,
          zIndex: nextZ + 1,
          snappedZone: zone,
          preSnapGeometry: win.snappedZone
            ? win.preSnapGeometry
            : { position: win.position, size: win.size },
        };
      }),
    );
    setNextZ((z) => z + 1);
    setActiveId(id);
  }, [nextZ]);

  const unsnapWindow = useCallback((id: string) => {
    setWindows((prev) =>
      prev.map((win) => {
        if (win.id !== id || !win.snappedZone) return win;
        const geo = win.preSnapGeometry;
        return {
          ...win,
          position: geo ? geo.position : win.position,
          size: geo ? geo.size : win.size,
          snappedZone: undefined,
          preSnapGeometry: undefined,
        };
      }),
    );
  }, []);

  const minimizeAll = useCallback(() => {
    setWindows((prev) => {
      const hasVisible = prev.some((w) => w.status !== "minimized");
      if (hasVisible) {
        minimizedSnapshotRef.current = prev;
        return prev.map((w) => ({ ...w, status: "minimized" as const }));
      }
      return prev;
    });
    setActiveId(null);
  }, []);

  const restoreAll = useCallback(() => {
    if (minimizedSnapshotRef.current) {
      setWindows(minimizedSnapshotRef.current);
      const first = minimizedSnapshotRef.current.find((w) => w.status !== "minimized");
      if (first) setActiveId(first.id);
      minimizedSnapshotRef.current = null;
    } else {
      setWindows((prev) => prev.map((w) => ({ ...w, status: "open" as const })));
    }
  }, []);

  const closeAllWindows = useCallback(() => {
    setWindows([]);
    setActiveId(null);
  }, []);

  const cascadeWindows = useCallback(() => {
    setWindows((prev) => {
      const visible = prev.filter((w) => w.status !== "minimized");
      const minimized = prev.filter((w) => w.status === "minimized");
      return [
        ...visible.map((w, i) => ({
          ...w,
          status: "open" as const,
          position: cascadePosition(i),
          size: DEFAULT_SIZE,
          zIndex: BASE_Z + i + 1,
          snappedZone: null,
          preSnapGeometry: undefined,
        })),
        ...minimized,
      ];
    });
    setNextZ((z) => z + 20);
  }, []);

  const tileHorizontally = useCallback(() => {
    const screenW = typeof window !== "undefined" ? window.innerWidth : 1920;
    const screenH = typeof window !== "undefined" ? window.innerHeight - 48 : 1032;
    setWindows((prev) => {
      const visible = prev.filter((w) => w.status !== "minimized");
      const minimized = prev.filter((w) => w.status === "minimized");
      if (visible.length === 0) return prev;
      const tileH = Math.floor(screenH / visible.length);
      return [
        ...visible.map((w, i) => ({
          ...w,
          status: "open" as const,
          position: { x: 0, y: i * tileH },
          size: { width: screenW, height: tileH },
          zIndex: BASE_Z + i + 1,
          snappedZone: null,
          preSnapGeometry: undefined,
        })),
        ...minimized,
      ];
    });
    setNextZ((z) => z + 20);
  }, []);

  const tileVertically = useCallback(() => {
    const screenW = typeof window !== "undefined" ? window.innerWidth : 1920;
    const screenH = typeof window !== "undefined" ? window.innerHeight - 48 : 1032;
    setWindows((prev) => {
      const visible = prev.filter((w) => w.status !== "minimized");
      const minimized = prev.filter((w) => w.status === "minimized");
      if (visible.length === 0) return prev;
      const tileW = Math.floor(screenW / visible.length);
      return [
        ...visible.map((w, i) => ({
          ...w,
          status: "open" as const,
          position: { x: i * tileW, y: 0 },
          size: { width: tileW, height: screenH },
          zIndex: BASE_Z + i + 1,
          snappedZone: null,
          preSnapGeometry: undefined,
        })),
        ...minimized,
      ];
    });
    setNextZ((z) => z + 20);
  }, []);

  const ALWAYS_ON_TOP_Z = 9000;

  const setAlwaysOnTop = useCallback((id: string, on: boolean) => {
    setWindows((prev) =>
      prev.map((w) => {
        if (w.id !== id) return w;
        return { ...w, zIndex: on ? ALWAYS_ON_TOP_Z : nextZ + 1 };
      }),
    );
    if (!on) setNextZ((z) => z + 1);
  }, [nextZ]);

  const cycleWindow = useCallback((direction: "next" | "prev" = "next"): string | null => {
    const visibleWindows = windows.filter((w) => w.status !== "minimized");
    if (visibleWindows.length === 0) return null;

    const currentIndex = visibleWindows.findIndex((w) => w.id === activeId);
    let nextIndex: number;
    if (direction === "next") {
      nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % visibleWindows.length;
    } else {
      nextIndex = currentIndex < 0 ? visibleWindows.length - 1 : (currentIndex - 1 + visibleWindows.length) % visibleWindows.length;
    }
    const targetId = visibleWindows[nextIndex].id;
    focusWindow(targetId);
    return targetId;
  }, [windows, activeId, focusWindow]);

  const applyPreset = useCallback((presetId: string) => {
    const allPresets = getBuiltInPresets();
    const preset = allPresets.find(p => p.id === presetId);
    if (!preset) return;

    const newWindows: WindowState[] = preset.windows.map((pw, i) => {
      const app = getAppDef(pw.id);
      return {
        id: pw.id,
        title: app?.label ?? pw.id,
        icon: app?.icon ?? "",
        position: pw.position,
        size: pw.size,
        zIndex: BASE_Z + i + 1,
        status: pw.status,
      };
    });

    setWindows(newWindows);
    setNextZ(BASE_Z + newWindows.length + 1);
    setWindowCount(newWindows.length);
    const firstVisible = newWindows.find(w => w.status !== "minimized");
    setActiveId(firstVisible?.id ?? null);
  }, []);

  const saveCurrentLayout = useCallback((name: string) => {
    const layout: LayoutPreset = {
      id: `custom-${Date.now()}`,
      name,
      windows: windows.map(w => ({
        id: w.id,
        position: w.position,
        size: w.size,
        status: w.status,
      })),
    };

    try {
      const existing = JSON.parse(localStorage.getItem(STORAGE_KEY_LAYOUTS) || "[]");
      existing.push(layout);
      localStorage.setItem(STORAGE_KEY_LAYOUTS, JSON.stringify(existing));
    } catch { /* ignore */ }
  }, [windows]);

  const loadSavedLayouts = useCallback((): LayoutPreset[] => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY_LAYOUTS) || "[]");
    } catch {
      return [];
    }
  }, []);

  const deleteSavedLayout = useCallback((id: string) => {
    try {
      const existing: LayoutPreset[] = JSON.parse(localStorage.getItem(STORAGE_KEY_LAYOUTS) || "[]");
      const filtered = existing.filter(l => l.id !== id);
      localStorage.setItem(STORAGE_KEY_LAYOUTS, JSON.stringify(filtered));
    } catch { /* ignore */ }
  }, []);

  const applySavedPreset = useCallback((presetId: string) => {
    // Check built-in first
    const builtIn = getBuiltInPresets().find(p => p.id === presetId);
    if (builtIn) {
      applyPreset(presetId);
      return;
    }
    // Check saved
    const saved = loadSavedLayouts().find(p => p.id === presetId);
    if (saved) {
      const newWindows: WindowState[] = saved.windows.map((pw, i) => {
        const app = getAppDef(pw.id);
        return {
          id: pw.id,
          title: app?.label ?? pw.id,
          icon: app?.icon ?? "",
          position: pw.position,
          size: pw.size,
          zIndex: BASE_Z + i + 1,
          status: pw.status,
        };
      });
      setWindows(newWindows);
      setNextZ(BASE_Z + newWindows.length + 1);
      setWindowCount(newWindows.length);
      const firstVisible = newWindows.find(w => w.status !== "minimized");
      setActiveId(firstVisible?.id ?? null);
    }
  }, [applyPreset, loadSavedLayouts]);

  return (
    <WindowManagerContext.Provider
      value={{
        windows,
        activeId,
        initialized,
        openWindow,
        closeWindow,
        minimizeWindow,
        maximizeWindow,
        restoreWindow,
        focusWindow,
        moveWindow,
        resizeWindow,
        snapWindow,
        unsnapWindow,
        minimizeAll,
        restoreAll,
        closeAllWindows,
        cascadeWindows,
        tileHorizontally,
        tileVertically,
        setAlwaysOnTop,
        cycleWindow,
        applyPreset: applySavedPreset,
        saveCurrentLayout,
        loadSavedLayouts,
        deleteSavedLayout,
        getBuiltInPresets: () => getBuiltInPresets(),
      }}
    >
      {children}
    </WindowManagerContext.Provider>
  );
}

export function useWindowManager() {
  const ctx = useContext(WindowManagerContext);
  if (!ctx) throw new Error("useWindowManager must be used within WindowManagerProvider");
  return ctx;
}
